
import Vue from 'vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import pluralize from 'pluralize';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import {
	DiffFilters,
	DiffKind,
	DiffRow,
	SyncResultPayload,
	countByKind,
	filterDiffRows,
	groupOptions,
	teamOptions,
} from './scimDiff';

const PAGE_SIZE = 25;

/** Stage 03 and post-deploy result: one flat, filterable, singly paginated list of changes. */
export default Vue.extend({
	name: 'ScimDiffDialog',
	components: {
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
		BaseButton,
	},
	props: {
		value: { type: Boolean, default: false },
		payload: { type: Object as () => SyncResultPayload | null, default: null },
		rows: { type: Array as () => DiffRow[], default: () => [] },
		ranAt: { type: Number, default: 0 },
		deploying: { type: Boolean, default: false },
		/** False while the plan is dirty or a run is in flight: deploying from a stale diff would lie. */
		canDeploy: { type: Boolean, default: false },
	},
	data () {
		return {
			filters: { kind: 'all', group: '', team: '', search: '' } as DiffFilters,
			page: 0,
		};
	},
	computed: {
		...mapGetters([
			'displayLabels',
		]),
		counts () {
			return countByKind(this.rows);
		},
		chips (): Array<{ kind: DiffKind | 'all'; label: string; count: number }> {
			return [
				{ kind: 'all', label: 'All', count: this.counts.all },
				{ kind: 'removed', label: 'Removals', count: this.counts.removed },
				{ kind: 'added', label: 'Additions', count: this.counts.added },
				{ kind: 'role', label: 'Role changes', count: this.counts.role },
				{ kind: 'issue', label: 'Issues', count: this.counts.issue },
			];
		},
		groupFilterItems (): Array<{ text: string; value: string }> {
			return [
				{ text: 'All groups', value: '' },
				...groupOptions(this.rows).map((name: string) => ({ text: name, value: name })),
			];
		},
		teamFilterItems (): Array<{ text: string; value: string }> {
			return [
				{ text: `All ${pluralize(this.displayLabels.team)}`, value: '' },
				...teamOptions(this.rows).map((name: string) => ({ text: name, value: name })),
			];
		},
		filteredRows (): DiffRow[] {
			return filterDiffRows(this.rows, {
				...this.filters,
				search: this.filters.search || '',
			});
		},
		/** Drives the export label: the file follows the filters, so the button has to say when it will. */
		hasActiveFilters (): boolean {
			return this.filters.kind !== 'all'
				|| !!this.filters.group
				|| !!this.filters.team
				|| !!(this.filters.search || '').trim();
		},
		totalPages (): number {
			return Math.max(1, Math.ceil(this.filteredRows.length / PAGE_SIZE));
		},
		pagedRows (): DiffRow[] {
			const page = Math.min(this.page, this.totalPages - 1);
			return this.filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
		},
		paginationLabel (): string {
			const total = this.filteredRows.length;
			if (!total) return 'No rows';
			const page = Math.min(this.page, this.totalPages - 1);
			const start = page * PAGE_SIZE + 1;
			const end = Math.min((page + 1) * PAGE_SIZE, total);
			return `Showing ${start}-${end} of ${total}`;
		},
		dialogTitle (): string {
			if (!this.payload) return 'Sync result';
			if (!this.rows.length) return this.payload.dryRun ? 'Dry run complete' : 'Deploy complete';
			if (this.payload.dryRun) return `${this.counts.all} ${pluralize('change', this.counts.all)} the plan would make`;
			if (this.counts.issue > 0) return 'Deploy completed with issues';
			return 'Deploy completed';
		},
		emptyTitle (): string {
			return this.payload?.dryRun ? 'This plan would change nothing' : 'Nothing needed changing';
		},
		emptyCopy (): string {
			const when = this.ranAt ? moment(this.ranAt).format('h:mm a') : '';
			const teams = pluralize(this.displayLabels.team);
			return this.payload?.dryRun
				? `The dry run at ${when} compared your groups against your ${teams} and found no members to add, remove or re-role, and no issues to report. Your ${teams} already match your plan.`
				: `The deploy at ${when} found your ${teams} already matching your plan, so no members were added, removed or re-roled.`;
		},
		subtitle (): string {
			if (!this.payload) return '';
			const when = this.ranAt ? moment(this.ranAt).format('h:mm a') : '';
			if (this.payload.dryRun) {
				return `Dry run at ${when}. Nothing was written. The list below is what deploying this plan would change.`;
			}
			return `Deployed at ${when}. ${this.counts.added} added, ${this.counts.removed} removed.`;
		},
		closingNote (): string {
			if (!this.payload?.dryRun) return '';
			if (this.counts.removed > 0) {
				return `${this.counts.removed} ${pluralize('person', this.counts.removed)} ${this.counts.removed === 1 ? 'loses' : 'lose'} access the moment you deploy.`;
			}
			return 'No one loses access when you deploy this plan.';
		},
		/** Issues are reported, not applied, so they never count toward what a deploy would write. */
		deployableCount (): number {
			return this.counts.all - this.counts.issue;
		},
		deployLabel (): string {
			if (!this.deployableCount) return 'Deploy anyway';
			return `Deploy ${this.deployableCount} ${pluralize('change', this.deployableCount)}`;
		},
	},
	watch: {
		// A new run replaces the list underneath the filters; keeping page 3 of a shorter list shows nothing.
		rows () {
			this.page = 0;
			this.filters = { kind: 'all', group: '', team: '', search: '' };
		},
		filters: {
			deep: true,
			handler () {
				this.page = 0;
			},
		},
	},
	methods: {
		close () {
			this.$emit('input', false);
		},
		kindLabel (kind: DiffKind): string {
			if (kind === 'added') return 'Add';
			if (kind === 'removed') return 'Remove';
			if (kind === 'role') return 'Role';
			return 'Issue';
		},
		kindColor (kind: DiffKind): string {
			if (kind === 'added') return 'var(--emerald-500)';
			if (kind === 'removed') return 'var(--red-400)';
			if (kind === 'role') return 'var(--text-on-purple-background)';
			return 'var(--amber-500)';
		},
	},
});
